import React, { useMemo } from 'react';
import { Col, Form, Input, Row, Select, Slider } from 'antd';
import { FormattedMessage } from 'react-intl';

import ColorPicker from 'components/ColorPicker';

import { THEMES } from 'shared/constants/design';
import { EDIT_SURVEY_CSS } from 'shared/constants/permissions';

import SurveyPreview from 'components/SurveyPreview';
import get from 'get-value';
import LicenseTag from 'components/LicenseTag';
import useDataSource from 'hooks/useDataSource';
import InputInfoCircle from 'components/InputInfoCircle';
import messages from './messages';

const ThemeForm = ({ namePath }) => {
  const { dataSource, hasDataSourcePermission } = useDataSource();
  const theme = Form.useWatch([...namePath, 'theme']);

  const activeLicense = dataSource?.activeLicense;

  const canHaveCustomTheme = activeLicense?.settings.customTheme || theme === 'custom';
  const canEditTheme = theme === 'custom';

  const themeOptions = useMemo(() => {
    const options = [
      { value: 'default', label: 'Default' },
      { value: 'dark', label: 'Dark' },
    ];

    if (canHaveCustomTheme) {
      options.push({ value: 'custom', label: 'Custom' });
    } else {
      options.push({
        value: 'custom',
        label: (
          <>
            Custom
            {' '}
            <LicenseTag activeLicense={activeLicense} setting="customTheme" showTooltip popoverProps={{ placement: 'topRight' }} />
          </>
        ),
        disabled: true,
      });
    }

    return options;
  }, [canHaveCustomTheme]);

  return (
    <Row gutter={[16, 16]}>
      <Col span={8}>
        <Form.Item name={[...namePath, 'theme']} label="Theme">
          <Select options={themeOptions} />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Row gutter={16}>
          {Object.keys(THEMES.default).filter(key => key.indexOf('Color') !== -1).map(colorFormField => (
            <Col key={colorFormField} sm={24} md={8}>
              <Form.Item name={[...namePath, 'customTheme', colorFormField]}>
                <ColorPicker label={messages[colorFormField] ? <FormattedMessage {...messages[colorFormField]} /> : colorFormField} enableAlpha={false} disabled={!canEditTheme} />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Col>
      <Col span={24}>
        <Form.Item name={[...namePath, 'customTheme', 'cornerRadius']} label="Corner radius">
          <Slider max={16} marks={{ 0: '0px', 16: '16px' }} disabled={!canEditTheme} />
        </Form.Item>
      </Col>
      {theme === 'custom' && (
      <Col span={24}>
        <Form.Item name={[...namePath, 'customTheme', 'customCSS']} label="Custom CSS">
          <Input.TextArea autoSize={{ minRows: 5 }} disabled={!(canEditTheme && hasDataSourcePermission(EDIT_SURVEY_CSS))} />
        </Form.Item>
      </Col>
      )}
    </Row>
  );
};

const ThemeStep = ({ name }) => (
  <Row gutter={[16, 16]}>
    <Col span={16}>
      <h2>
        Survey theme
        <InputInfoCircle title={<FormattedMessage {...messages.themeHelpText} />} iconStyle={{ marginLeft: 8, width: 16 }} />
      </h2>

      <ThemeForm namePath={[name]} />
    </Col>

    <Col span={8} style={{ display: 'flex', minHeight: 500 }}>
      <Form.Item noStyle shouldUpdate={(prevValues, curValues) => get(prevValues, name) !== get(curValues, name)}>
        {({ getFieldsValue }) => {
          const value = getFieldsValue([name]);
          return <SurveyPreview {...get(value, name)} />;
        }}
      </Form.Item>
    </Col>
  </Row>
);

export default ThemeStep;
